import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Camera } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import Lightbox from "@/components/Lightbox";
import SkylineDivider from "@/components/SkylineDivider";
import { COTTAGES } from "@/lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Stay() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    party_size: "",
    arrival_date: "",
    departure_date: "",
    lodging_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryCottage, setGalleryCottage] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const openGallery = (cottage) => {
    setGalleryCottage(cottage);
    setGalleryIndex(0);
    setGalleryOpen(true);
  };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, party_size: form.party_size ? parseInt(form.party_size, 10) : null };
      await axios.post(`${API}/reservations`, payload);
      toast.success("Inquiry received — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", party_size: "", arrival_date: "", departure_date: "", lodging_interest: "", message: "" });
    } catch (err) {
      toast.error("Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="stay-page">
      <section className="container-x pt-12 lg:pt-24 pb-12" data-testid="stay-hero">
        <FadeIn><p className="overline mb-5">Stay With Us</p></FadeIn>
        <FadeIn delay={120}>
          <h1 className="font-serif text-5xl lg:text-7xl text-moss font-light leading-[1.02] tracking-tight max-w-4xl">
            Six historic cottages.
            <br />
            <em className="not-italic text-clay">One unhurried stay.</em>
          </h1>
        </FadeIn>
        <FadeIn delay={240}>
          <p className="mt-8 max-w-2xl text-lg text-coal/70 leading-[1.85]">
            Spread across 50 acres, each cottage has its own personality — some off-grid,
            some by the creek, some perched on a hilltop. Most are blissfully free of modern
            distractions. The sounds of the creek at night, starlit skies wide enough to feel
            them, and the kind of quiet that finds you before you know you were looking for it.
            Available individually, or together for a small group of up to fourteen guests.
          </p>
        </FadeIn>
        <FadeIn delay={360}>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/amenities" className="btn-ghost" data-testid="stay-amenities-link">
              Beyond the cottage →
            </Link>
            <Link to="/local-guide" className="btn-ghost" data-testid="stay-local-guide-link">
              Local guide →
            </Link>
          </div>
        </FadeIn>
      </section>

      {/* COTTAGES */}
      <section className="container-x pb-24 lg:pb-28 space-y-24 lg:space-y-32" data-testid="stay-cottages">
        {COTTAGES.map((c, i) => (
          <div key={c.slug} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-testid={`cottage-${c.slug}`}>
            <FadeIn className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="relative">
                <img src={c.image} alt={c.name} className="w-full aspect-[4/3] lg:aspect-[5/4] object-cover" />
                {c.imageAlt && (
                  <img src={c.imageAlt} alt={`${c.name} interior`} className="hidden lg:block absolute -bottom-10 -right-10 w-1/3 aspect-square object-cover border-8 border-cream" />
                )}
                {c.placeholder && (
                  <span className="absolute top-3 left-3 bg-cream/95 text-coal text-xs tracking-wide px-3 py-1.5 font-medium" data-testid={`cottage-placeholder-${c.slug}`}>
                    Photos coming soon
                  </span>
                )}
              </div>
            </FadeIn>
            <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <FadeIn delay={120}>
                <p className="overline mb-4">{c.sleeps && `Sleeps ${c.sleeps.toLowerCase()}`}</p>
                <h2 className="font-serif text-4xl lg:text-5xl text-moss font-light leading-tight tracking-tight">{c.name}</h2>
                <p className="mt-3 text-clay italic">{c.tagline}</p>
                <p className="mt-6 text-coal/75 text-lg leading-[1.85]">{c.description}</p>
                <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-coal/60">
                  {c.amenities.map((a) => (
                    <li key={a} className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-clay rounded-full" />
                      {a}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to={`/book/${c.slug}`}
                    className="btn-primary inline-flex items-center"
                    data-testid={`cottage-book-${c.slug}`}
                  >
                    Reserve / Inquire →
                  </Link>
                  {c.gallery && c.gallery.length > 0 && (
                    <button
                      type="button"
                      onClick={() => openGallery(c)}
                      className="btn-ghost inline-flex items-center gap-2"
                      data-testid={`cottage-gallery-${c.slug}`}
                    >
                      <Camera size={16} strokeWidth={1.5} />
                      View {c.gallery.length} photos
                    </button>
                  )}
                </div>
              </FadeIn>
            </div>
          </div>
        ))}
      </section>

      <SkylineDivider variant="moss" height={90} opacity={0.5} className="bg-cream" data-testid="stay-skyline-divider" />

      {/* AMENITIES CTA — drive curiosity to the full amenities page */}
      <section className="bg-ink text-cream py-20 lg:py-28 relative overflow-hidden" data-testid="stay-amenities-cta">
        <div className="container-narrow text-center relative">
          <FadeIn>
            <p className="overline text-cream/50 mb-5">A cottage is just the beginning</p>
            <h2 className="font-serif text-4xl lg:text-6xl font-light leading-[1.05] tracking-tight max-w-3xl mx-auto">
              Your stay opens
              <br />
              <em className="italic text-cream/75">the entire farm.</em>
            </h2>
          </FadeIn>
          <FadeIn delay={140}>
            <p className="mt-8 text-cream/75 text-lg leading-[1.85] max-w-2xl mx-auto">
              The barn, the sauna in the old corn crib, a quiet cottage just for reading, the
              yoga studio, the gym, fire pits, gardens, and miles of trail —
              all of it is yours to use, at no extra cost, with a little advance notice so we
              can keep things uncrowded for everyone.
            </p>
          </FadeIn>
          <FadeIn delay={260}>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link to="/amenities" className="btn-primary-light" data-testid="stay-cta-amenities-btn">
                See everything included →
              </Link>
              <Link to="/local-guide" className="btn-ghost-light" data-testid="stay-cta-local-guide-btn">
                Local guide
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="inquire" className="bg-oat py-24 lg:py-32 scroll-mt-24" data-testid="stay-inquiry-section">
        <div className="container-narrow">
          <FadeIn>
            <p className="overline mb-5 text-center">Reserve / Inquire</p>
            <h2 className="font-serif text-4xl lg:text-6xl text-moss font-light leading-[1.05] tracking-tight text-center">
              Tell us a little about your stay.
            </h2>
            <p className="mt-6 text-coal/70 text-lg leading-[1.85] text-center max-w-xl mx-auto">
              We'll respond personally within a day or two. Listings on Airbnb and VRBO
              are coming soon — for now, an email or a quick form is the best way in.
            </p>
          </FadeIn>

          <FadeIn delay={180}>
            <form onSubmit={onSubmit} className="mt-16 space-y-10" data-testid="reservation-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label className="label-clean">Your name</label>
                  <input required name="name" value={form.name} onChange={onChange} className="input-clean" data-testid="reservation-name-input" />
                </div>
                <div>
                  <label className="label-clean">Email</label>
                  <input required type="email" name="email" value={form.email} onChange={onChange} className="input-clean" data-testid="reservation-email-input" />
                </div>
                <div>
                  <label className="label-clean">Phone (optional)</label>
                  <input name="phone" value={form.phone} onChange={onChange} className="input-clean" data-testid="reservation-phone-input" />
                </div>
                <div>
                  <label className="label-clean">Party size</label>
                  <input type="number" min="1" max="50" name="party_size" value={form.party_size} onChange={onChange} className="input-clean" data-testid="reservation-party-input" />
                </div>
                <div>
                  <label className="label-clean">Arrival date</label>
                  <input type="date" name="arrival_date" value={form.arrival_date} onChange={onChange} className="input-clean" data-testid="reservation-arrival-input" />
                </div>
                <div>
                  <label className="label-clean">Departure date</label>
                  <input type="date" name="departure_date" value={form.departure_date} onChange={onChange} className="input-clean" data-testid="reservation-departure-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="label-clean">Which cottage interests you?</label>
                  <select name="lodging_interest" value={form.lodging_interest} onChange={onChange} className="input-clean" data-testid="reservation-lodging-select">
                    <option value="">No preference — recommend something</option>
                    {COTTAGES.map((c) => (
                      <option key={c.slug}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label-clean">A note about your stay</label>
                  <textarea required name="message" value={form.message} onChange={onChange} rows={5} className="input-clean resize-none" placeholder="Anniversary, retreat, family gathering, or just need quiet — tell us a little." data-testid="reservation-message-input" />
                </div>
              </div>

              <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50" data-testid="reservation-submit-btn">
                {loading ? "Sending…" : "Send inquiry"}
              </button>
            </form>
          </FadeIn>
        </div>
      </section>

      <Lightbox
        open={galleryOpen}
        images={galleryCottage?.gallery || []}
        index={galleryIndex}
        title={galleryCottage?.name}
        onClose={() => setGalleryOpen(false)}
        onIndexChange={setGalleryIndex}
      />
    </div>
  );
}
